<template>
  <!-- Trigger: always in the DOM so it can serve as the positioning anchor -->
  <span ref="triggerEl" @click="openPopover" style="cursor:pointer;">
    {{ modelValue }}
  </span>

  <!-- Teleport popover to body so it escapes bar overflow/clipping -->
  <Teleport to="body">
    <div v-if="isOpen" ref="popoverEl" class="popover" :style="dropdownStyle">
      <div class="popover-content">
        <slot name="input">
          <input
            v-model="tempValue"
            :type="type"
            v-bind="attrs"
          />
        </slot>
        <button class="icon-btn" @click="cancel" aria-label="Cancel" title="Cancel"><X :size="14" /></button>
        <button class="icon-btn" @click="confirm" aria-label="Confirm" title="Confirm"><Check :size="14" /></button>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, useAttrs, defineOptions } from 'vue';
import { Check, X } from "lucide-vue-next";
import { useAnchoredDropdown } from "@/composables/useAnchoredDropdown";

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  modelValue: number | string
}>();

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel']);

const originalAttrs = useAttrs();
const type = computed<string>(
    () => (
        typeof originalAttrs.type === 'string'
        && originalAttrs.type
            ? originalAttrs.type
            : 'text'
    )
);
const attrs = computed(
  () => (
    Object.fromEntries(
      Object.entries(originalAttrs)
        .filter(([key]) => key !== 'type')
    )
  )
);

const triggerEl = ref<HTMLElement | null>(null);
const popoverEl = ref<HTMLElement | null>(null);
const { isOpen, dropdownStyle, open, close } = useAnchoredDropdown(triggerEl, popoverEl, {
  minWidth: 180,
  maxWidth: 280,
  // Popover stays open until confirm/cancel; outside clicks intentionally not handled
  closeOnOutsideClick: false,
});

const tempValue = ref(props.modelValue);

function openPopover() {
  tempValue.value = props.modelValue;
  open();
}

function cancel() {
  close();
  emit('cancel');
}

function confirm() {
  emit('update:modelValue', tempValue.value);
  emit('confirm', tempValue.value);
  close();
}
</script>

<style scoped lang="scss">
.popover {
  /* position:fixed + coordinates injected via :style from useAnchoredDropdown */
  background: var(--bg-surface, white);
  border: 1px solid var(--border, #ccc);
  border-radius: 8px;
  padding: 0.5em 1em 0.7em 1em;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  display: inline-block;
}

.popover-content {
  display: flex;
  align-items: center;
  gap: 0.5em;
}

.icon-btn {
  background: none;
  border: none;
  font-size: 1.2em;
  cursor: pointer;
  padding: 0 0.2em;
  color: var(--text-primary, inherit);

  &:hover { color: var(--accent, #7f7); }
}
</style>
